import React, { useEffect, useState } from "react";
import { confidenceLabel, formatValue, hostOf } from "./proposalFormat";

// One probable double entry: the event that stays next to the one that would be removed, with
// the facts that tell them apart. Removing deletes the event for good, so it asks once more.

const ROWS = [
  { key: "title", label: "Titel" },
  { key: "eventDate", label: "Datum", format: (v) => formatValue("eventDate", v) },
  { key: "time", label: "Zeit", value: (e) => `${formatValue("startTime", e.startTime)}–${formatValue("endTime", e.endTime)}` },
  { key: "address", label: "Adresse" },
  { key: "instagramName", label: "Instagram", format: (v) => formatValue("instagramName", v) },
  { key: "ticketUrl", label: "Ticket-Link", format: (v) => (v ? hostOf(v) : "—") },
  { key: "artists", label: "Line-up", value: (e) => (e.artists?.length ? e.artists.join(", ") : "—") },
  { key: "approved", label: "Status", value: (e) => (e.approved ? "Freigegeben" : "Wartet auf Freigabe") },
  { key: "confirmations", label: "Zusagen", value: (e) => String(e.confirmations ?? 0) },
  { key: "views", label: "Aufrufe", value: (e) => String(e.views ?? 0) },
];

const cell = (row, e) => {
  if (!e) return "—";
  if (row.value) return row.value(e);
  const v = e[row.key];
  if (v == null || v === "") return "—";
  return row.format ? row.format(v) : String(v);
};

function Side({ label, tone, event, other }) {
  return (
    <div className={`dq-dup-side dq-dup-side--${tone}`}>
      <div className="dq-dup-side-head">
        <span className="dq-dup-side-label">{label}</span>
        {event && <span className="dq-muted">ID {event.id}</span>}
      </div>
      {!event ? (
        <p className="dq-muted">Gibt es nicht mehr.</p>
      ) : (
        <dl className="dq-dup-facts">
          {ROWS.map((row) => {
            const value = cell(row, event);
            const differs = other && value !== cell(row, other);
            return (
              <div key={row.key} className={`dq-dup-fact${differs ? " dq-dup-fact--differs" : ""}`}>
                <dt>{row.label}</dt>
                <dd title={value}>{value}</dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

export default function DuplicateCard({ p, state, onRemove, onKeepOther, onReject }) {
  const [confirm, setConfirm] = useState(null); // "remove" | "other"
  useEffect(() => {
    if (!confirm) return undefined;
    const t = setTimeout(() => setConfirm(null), 6000);
    return () => clearTimeout(t);
  }, [confirm]);

  const keep = p.relatedCurrent ?? p.newValue;
  const remove = p.currentValue ?? p.oldValue;
  const busy = state?.status === "saving";
  const decided = state?.status === "ACCEPTED" || state?.status === "REJECTED";

  if (decided) {
    return (
      <section className={`dq-dup dq-dup--decided dq-row--${state.status.toLowerCase()}`} aria-label={`Duplikat ${p.markerTitle}`}>
        <span className="dq-field">Duplikat</span>
        <span className="dq-decided">
          {state.status === "REJECTED"
            ? "Kein Duplikat – beide Events bleiben"
            : `Entfernt: ${state.removedTitle ?? remove?.title} (ID ${state.removedId ?? remove?.id})`}
        </span>
      </section>
    );
  }

  const lost = (entry) => Number(entry?.confirmations || 0);
  const blocked = p.eventMissing;

  return (
    <section className="dq-dup" aria-label={`Duplikat ${p.markerTitle}`}>
      <header className="dq-dup-head">
        <span className="dq-dup-tag">Mögliches Duplikat</span>
        <span className={`dq-badge dq-badge--${String(p.confidence).toLowerCase()}`}>{confidenceLabel(p.confidence)}</span>
        <span className="dq-event-date">{formatValue("eventDate", p.eventDate)}</span>
      </header>

      <div className="dq-dup-grid">
        <Side label="Bleibt" tone="keep" event={keep} other={remove} />
        <Side label="Wird entfernt" tone="remove" event={remove} other={keep} />
      </div>

      <p className="dq-reason">{p.reason}</p>
      {p.sources?.length > 0 && (
        <div className="dq-sources">
          {p.sources.map((s) => (
            <a key={s} href={s} target="_blank" rel="noreferrer" className="dq-source" title={s}>{hostOf(s)}</a>
          ))}
        </div>
      )}

      {lost(remove) > 0 && !blocked && (
        <p className="dq-warning" role="note">
          {lost(remove)} {lost(remove) === 1 ? "Person hat" : "Personen haben"} beim Event, das entfernt wird, zugesagt – diese Zusagen gehen verloren.
        </p>
      )}
      {blocked && <p className="dq-warning" role="note">Eines der beiden Events gibt es nicht mehr – nur Ablehnen ist möglich.</p>}
      {state?.error && <p className="dq-error" role="alert">{state.error}</p>}

      <div className="dq-actions dq-actions--dup">
        <button type="button" className="dq-btn dq-btn--reject" onClick={onReject} disabled={busy}>Kein Duplikat</button>
        {confirm === "other" ? (
          <button type="button" className="dq-btn dq-btn--danger" onClick={() => { setConfirm(null); onKeepOther(); }} disabled={busy}>
            Wirklich „{keep?.title}“ entfernen?
          </button>
        ) : (
          <button type="button" className="dq-btn dq-btn--ghost" onClick={() => setConfirm("other")} disabled={busy || blocked}>
            Andersrum: rechts behalten
          </button>
        )}
        {confirm === "remove" ? (
          <button type="button" className="dq-btn dq-btn--danger" onClick={() => { setConfirm(null); onRemove(); }} disabled={busy}>
            {busy ? <span className="dq-spinner" aria-hidden="true" /> : null}
            Endgültig entfernen
          </button>
        ) : (
          <button type="button" className="dq-btn dq-btn--accept" onClick={() => setConfirm("remove")} disabled={busy || blocked}>
            {busy ? <span className="dq-spinner" aria-hidden="true" /> : null}
            Duplikat entfernen
          </button>
        )}
      </div>
    </section>
  );
}
